import React, { useCallback, useMemo } from 'react';

import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLoyaltyBoosters } from 'src/app/hooks/discount/use-loyalty-boosters';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';

import { calculationMethodOptions, CalculationMethods, DiscountAppliesTo } from '../../../models/discount-models';
import { AppliedFilters } from '../../shared/applied-filters';
import { Condition } from '../../shared/condition';
import { FilteredProductCount } from '../../shared/filtered-product-count';
import { percentCalculationMethods } from '../../shared/helpers';
import { useDiscountWizardStateContext } from '../../state';
import { useDiscountAppliesToOptions } from '../rewards/use-discount-applies-to-options';

import { AppliedFiltersWrapper } from './styled';
import { SummaryCard } from './summary-card';

import type { SummaryCardProps } from './summary-card';

export function RewardCard({ onEditClick }: { onEditClick: () => void }) {
  const {
    rewardsStepState: { discountMethod, percentOff, amountOff, discountAppliesTo, allowStacking, loyaltyMultiplier },
  } = useDiscountWizardStateContext();
  const allowLoyaltyBoosters = useLoyaltyBoosters();
  const useDiscountsLoyaltyMultiplierFix = useLDFlag(LD.DISCOUNTS_LOYALTY_MULTIPLIER_FIX, false);

  const { discountAppliesToOptions } = useDiscountAppliesToOptions();

  const isPercentOff = percentCalculationMethods.includes(discountMethod);
  const selectRewardProducts = discountAppliesTo === DiscountAppliesTo.DifferentProducts;

  const getRewardDetailsByMethodType = useCallback(() => {
    const discountIsPriceToAmount =
      discountMethod === CalculationMethods.PriceToAmount || discountMethod === CalculationMethods.PriceToAmountTotal;

    const amountOffFormatted = `$${amountOff}`;
    const percentOffFormatted = `${percentOff}%`;

    if (
      allowLoyaltyBoosters &&
      useDiscountsLoyaltyMultiplierFix &&
      discountMethod === CalculationMethods.LoyaltyMultiplier
    ) {
      return { title: 'Multiplier:', value: loyaltyMultiplier };
    }
    if (
      allowLoyaltyBoosters &&
      !useDiscountsLoyaltyMultiplierFix &&
      discountMethod === CalculationMethods.LoyaltyMultiplier
    ) {
      return { title: 'Multiplier:', value: amountOff };
    }
    if (discountIsPriceToAmount) {
      return { title: 'Price to amount:', value: amountOffFormatted };
    }
    return {
      title: isPercentOff ? 'Percent off:' : 'Amount off:',
      value: isPercentOff ? percentOffFormatted : amountOffFormatted,
    };
  }, [
    amountOff,
    discountMethod,
    isPercentOff,
    percentOff,
    loyaltyMultiplier,
    useDiscountsLoyaltyMultiplierFix,
    allowLoyaltyBoosters,
  ]);

  const rows = useMemo(() => {
    const rows: SummaryCardProps['rows'] = [
      {
        fieldTitle: 'Discount method:',
        fieldValue: calculationMethodOptions.find((method) => method.val === discountMethod)?.name || '',
      },
      {
        fieldTitle: getRewardDetailsByMethodType().title,
        fieldValue: getRewardDetailsByMethodType().value,
      },
      {
        fieldTitle: 'Applied to:',
        fieldValue: discountAppliesToOptions.find((option) => option.val === discountAppliesTo)?.name || '',
      },
      {
        fieldTitle: 'Allow stacking:',
        fieldValue: allowStacking ? 'Yes' : 'No',
      },
    ];

    if (selectRewardProducts) {
      rows.push(
        {
          fieldTitle: 'Reward',
          fieldValue: '',
        },
        {
          fieldTitle: 'Condition:',
          fieldValue: <Condition requirement='reward' />,
        },
        {
          fieldTitle: 'Filters applied:',
          fieldValue: (
            <AppliedFiltersWrapper>
              <AppliedFilters excludeCommas requirement='reward' />
            </AppliedFiltersWrapper>
          ),
        },
        {
          fieldTitle: 'Eligible products:',
          fieldValue: <FilteredProductCount requirement='reward' />,
        }
      );
    }
    return rows;
  }, [
    allowStacking,
    discountAppliesTo,
    discountAppliesToOptions,
    discountMethod,
    getRewardDetailsByMethodType,
    selectRewardProducts,
  ]);

  return <SummaryCard rows={rows} title='Reward' onEditClick={onEditClick} />;
}
